import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import {} from '@ibe/types';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import {} from 'react';
import { cn } from '../lib/cn.js';
import { EmptyState, ErrorState, LoadingState } from './states.js';
function nextDirection(current, columnId) {
    // A new column always starts ascending. Carrying the previous column's
    // direction over means sorting by "name" after sorting "created" descending
    // silently lists Z first, which reads as a broken sort.
    if (current?.columnId !== columnId)
        return 'asc';
    return current.direction === 'asc' ? 'desc' : 'asc';
}
function ariaSortFor(column, sort) {
    if (column.sortable !== true)
        return undefined;
    if (sort?.columnId !== column.id)
        return 'none';
    return sort.direction === 'asc' ? 'ascending' : 'descending';
}
/**
 * A table with sorting, and with the three non-data states handled in one
 * place.
 *
 * The ordering of those states is the important part: **loading is checked
 * before empty**. An empty state rendered while a request is still in flight
 * tells the user there are no properties, and the one who believes it creates
 * a duplicate. Error is checked before both, because stale-looking data is
 * worse than an acknowledged failure.
 *
 * There is deliberately no `onRowClick`. A clickable `<tr>` cannot be reached
 * by keyboard, is announced as nothing by a screen reader, and cannot be
 * middle-clicked or opened in a new tab — all of which matter for a table
 * whose rows are records someone opens dozens of times a day. Put a link in a
 * cell instead.
 */
export function DataTable({ caption, columns, rows, rowKey, loading = false, error, onRetry, sort, onSortChange, emptyTitle = 'Nothing to show', emptyDescription, emptyAction, showCaption = false, className, }) {
    if (error !== undefined && error !== '') {
        return _jsx(ErrorState, { description: error, ...(onRetry === undefined ? {} : { onRetry }) });
    }
    if (loading) {
        return _jsx(LoadingState, { label: `Loading ${caption.toLowerCase()}…` });
    }
    if (rows.length === 0) {
        return (_jsx(EmptyState, { title: emptyTitle, ...(emptyDescription === undefined ? {} : { description: emptyDescription }), ...(emptyAction === undefined ? {} : { action: emptyAction }) }));
    }
    return (_jsx("div", { className: cn('w-full overflow-x-auto', className), children: _jsxs("table", { className: "w-full caption-bottom text-sm", children: [_jsx("caption", { className: showCaption ? 'py-2 text-sm text-muted-foreground' : 'sr-only', children: caption }), _jsx("thead", { className: "border-b bg-surface", children: _jsx("tr", { children: columns.map((column) => {
                            const sortable = column.sortable === true && onSortChange !== undefined;
                            const active = sort?.columnId === column.id;
                            return (_jsx("th", { scope: "col", "aria-sort": ariaSortFor(column, sort), className: cn('h-11 px-3 text-left align-middle text-sm font-medium text-text-muted', column.align === 'right' && 'text-right', column.className), children: sortable ? (_jsxs("button", { type: "button", onClick: () => {
                                        onSortChange({
                                            columnId: column.id,
                                            direction: nextDirection(sort, column.id),
                                        });
                                    }, className: "inline-flex items-center gap-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", children: [column.header, active ? (sort.direction === 'asc' ? (_jsx(ArrowUp, { className: "h-3.5 w-3.5", "aria-hidden": "true" })) : (_jsx(ArrowDown, { className: "h-3.5 w-3.5", "aria-hidden": "true" }))) : (_jsx(ChevronsUpDown, { className: "h-3.5 w-3.5 opacity-50", "aria-hidden": "true" }))] })) : (_jsxs(_Fragment, { children: [column.header, column.headerLabel === undefined ? null : (_jsx("span", { className: "sr-only", children: column.headerLabel }))] })) }, column.id));
                        }) }) }), _jsx("tbody", { children: rows.map((row) => (_jsx("tr", { className: "border-b last:border-0 hover:bg-surface-muted/70", children: columns.map((column) => (_jsx("td", { className: cn('px-3 py-3.5 align-middle text-sm text-text', column.align === 'right' && 'text-right', column.className), children: column.cell(row) }, column.id))) }, rowKey(row)))) })] }) }));
}
//# sourceMappingURL=data-table.js.map